import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { balanceStatusKey, BALANCE_STATUS_DEFAULTS } from "../../utils/balanceStatus";
import { useTheme } from "../../context/ThemeContext";
import { getPageBackground, getPrimaryText, getSecondaryText, getMutedText, getLightBorder } from "../../utils/themeUtils";
import { Button } from "../ui";
import PaymentModal from "./PaymentModal";
import { useTenantFormat } from "../../hooks/useTenantFormat";

interface BalanceProps {
  balance: number; // positive or negative balance amount
  futureInvoicesTotal?: number; // total of future invoices not yet due
  widthClass?: string;
  showPaymentLink?: boolean; // whether to show "Make a Payment" link
  onPaymentSuccess?: () => void; // callback when payment succeeds (to refresh balance)
}

const BalanceDisplay: React.FC<BalanceProps> = ({ balance, futureInvoicesTotal, widthClass, showPaymentLink = false, onPaymentSuccess }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation('membership');
  const { formatCurrency } = useTenantFormat();
  const [showBalance, setShowBalance] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const isPositive = balance >= 0;
  const hasFutureInvoices = futureInvoicesTotal !== undefined && futureInvoicesTotal > 0;

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowBalance(true);
    }, 3000); // 3 seconds

    return () => clearTimeout(timer);
  }, []);

  const handlePaymentSuccess = () => {
    onPaymentSuccess?.();
  };

  return (
    <>
      <div
        className={`p-4 ${getPageBackground(isDarkMode)} rounded-lg border ${getLightBorder(isDarkMode)} h-full ${
          widthClass || "md:w-1/3 w-full"
        }`}
      >
        <div className="flex items-center justify-between">
          {/* Left side - Balance info */}
          <div className="flex-1">
            <h2 className={`text-xl font-semibold mb-2 ${getPrimaryText(isDarkMode)}`}>{t('billing.accountBalance')}</h2>

            {showBalance ? (
              <>
                <p
                  className={`text-3xl font-bold ${
                    isPositive
                      ? isDarkMode ? "text-green-400" : "text-green-600"
                      : isDarkMode ? "text-red-400" : "text-red-600"
                  }`}
                >
                  {isPositive ? "" : "-"}
                  {formatCurrency(Math.abs(balance))}
                </p>
                <p className={`mt-1 ${getSecondaryText(isDarkMode)}`}>
                  {t(`billing.${balanceStatusKey(balance)}`, { defaultValue: BALANCE_STATUS_DEFAULTS[balanceStatusKey(balance)] })}
                </p>

                {/* Show future invoices total if any exist */}
                {hasFutureInvoices && (
                  <p className={`text-sm ${getMutedText(isDarkMode)} mt-2`}>
                    {t('billing.upcoming')}: <span className={`font-semibold ${isDarkMode ? "text-orange-400" : "text-orange-600"}`}>-{formatCurrency(futureInvoicesTotal)}</span>
                  </p>
                )}
              </>
            ) : (
              <p className={`text-sm italic ${getMutedText(isDarkMode)}`}>{t('billing.loadingBalance')}</p>
            )}
          </div>

          {/* Right side - Make a Payment button, vertically centered */}
          {showPaymentLink && showBalance && (
            <div className="flex-shrink-0 ml-4">
              <Button
                variant="primary"
                size="md"
                onClick={() => setIsPaymentModalOpen(true)}
                icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                }
              >
                {t('billing.makeAPayment', { defaultValue: 'Make a Payment' })}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onPaymentSuccess={handlePaymentSuccess}
      />
    </>
  );
};

export default BalanceDisplay;
